import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { GuideStepKey } from '../../types/OnboardingGuide.types';
import { logout } from './authSlice';

const TOTAL_TUTORIAL_GUIDE_STEPS = 12;
const FIRST_TUTORIAL_GUIDE_STEP_KEY: GuideStepKey = 'WELCOME';

export interface TutorialGuideState {
  sessionId: string | null;
  currentStepKey: GuideStepKey;
  isOpen: boolean;
  hasResolvedBootstrap: boolean;
  hasTutorialBannerExpired: boolean;
  canShowTutorialBannerFromOnboarding: boolean;
  showTutorialBanner: boolean;
}

const initialState: TutorialGuideState = {
  currentStepKey: FIRST_TUTORIAL_GUIDE_STEP_KEY,
  isOpen: false,
  hasResolvedBootstrap: false,
  sessionId: null,
  hasTutorialBannerExpired: false,
  canShowTutorialBannerFromOnboarding: true,
  showTutorialBanner: false
};

export function getTutorialGuideProgressPercentage(currentStepNumber: number) {
  return Math.round((currentStepNumber / TOTAL_TUTORIAL_GUIDE_STEPS) * 100);
}

export const tutorialGuideSlice = createSlice({
  name: 'tutorialGuide',
  initialState,
  reducers: {
    openTutorialGuide: (
      state,
      action: PayloadAction<GuideStepKey | undefined>
    ) => {
      const requestedStepKey = action.payload;

      if (requestedStepKey !== undefined) {
        state.currentStepKey = requestedStepKey;
        state.isOpen = true;
      }
    },
    setTutorialGuideBootstrapResolved: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.hasResolvedBootstrap = action.payload;
    },
    closeTutorialGuide: (state) => {
      state.isOpen = false;
    },
    setCurrentTutorialGuideStep: (
      state,
      action: PayloadAction<GuideStepKey>
    ) => {
      state.currentStepKey = action.payload;
    },
    setTutorialGuideSessionId: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.sessionId = action.payload;
    },
    setShowTutorialBanner: (state, action: PayloadAction<boolean>) => {
      state.showTutorialBanner = action.payload;
    },
    setTutorialBannerExpired: (state, action: PayloadAction<boolean>) => {
      state.hasTutorialBannerExpired = action.payload;
    },
    setCanShowTutorialBannerFromOnboarding: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.canShowTutorialBannerFromOnboarding = action.payload;
    },
    resetTutorialGuide: (state) => {
      state.isOpen = false;
      state.currentStepKey = FIRST_TUTORIAL_GUIDE_STEP_KEY;
      state.hasResolvedBootstrap = false;
      state.hasTutorialBannerExpired = false;
      state.canShowTutorialBannerFromOnboarding = true;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(logout, () => initialState);
  }
});

export const {
  openTutorialGuide,
  setTutorialGuideBootstrapResolved,
  closeTutorialGuide,
  setCurrentTutorialGuideStep,
  resetTutorialGuide,
  setTutorialGuideSessionId,
  setShowTutorialBanner,
  setTutorialBannerExpired,
  setCanShowTutorialBannerFromOnboarding
} = tutorialGuideSlice.actions;

export default tutorialGuideSlice.reducer;
